import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface DragDropData {
  nodeId: string;
  nodeName: string;
  operationId: string;
  serializedData: any;
  position: { x: number; y: number };
  originalPosition?: { x: number; y: number };
  altKeyPressed?: boolean;
  sourceElement?: HTMLElement;
}

export interface DragState {
  isDragging: boolean;
  dragData: DragDropData | null;
  mousePosition: { x: number; y: number };
  altKeyPressed?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DragDropService {
  private readonly dragState$ = new BehaviorSubject<DragState>({
    isDragging: false,
    dragData: null,
    mousePosition: { x: 0, y: 0 }
  });

  private readonly dropZones = new Map<HTMLElement, { callback: (data: DragDropData) => void }>();
  private dragElement: HTMLElement | null = null;
  private isDragging = false;
  private altKeyPressed = false;

  constructor() {
    document.addEventListener('keydown', (event) => {
      if (event.altKey) {
        this.altKeyPressed = true;
      }
    });

    document.addEventListener('keyup', (event) => {
      if (!event.altKey) {
        this.altKeyPressed = false;
      }
    });

    document.addEventListener('mousemove', (event) => {
      if (this.isDragging) {
        this.updateDragElementPosition(event.clientX, event.clientY);
        this.updateDropZonesFeedback(event.clientX, event.clientY);

        this.dragState$.next({
          isDragging: true,
          dragData: this.dragState$.value.dragData,
          mousePosition: { x: event.clientX, y: event.clientY },
          altKeyPressed: this.altKeyPressed || event.altKey
        });
      }
    });

    document.addEventListener('mouseup', (event) => {
      if (this.isDragging) {
        this.endDrag(event.clientX, event.clientY);
      }
    });
  }

  startDrag(data: DragDropData, sourceElement: HTMLElement): void {
    this.isDragging = true;

    const enrichedData = {
      ...data,
      originalPosition: {
        x: data.position.x,
        y: data.position.y
      },
      altKeyPressed: this.altKeyPressed,
      sourceElement
    };

    this.dragState$.next({
      isDragging: true,
      dragData: enrichedData,
      mousePosition: { x: data.position.x, y: data.position.y },
      altKeyPressed: this.altKeyPressed
    });

    this.createDragElement(enrichedData, sourceElement);

    document.body.classList.add('is-dragging');
  }

  endDrag(x: number, y: number): void {
    if (!this.isDragging) return;

    const currentState = this.dragState$.value;
    const dropTarget = this.getDropTargetAtPosition(x, y);
    const isAltPressed = currentState.altKeyPressed || this.altKeyPressed;

    if (dropTarget && currentState.dragData) {
      const dropZoneData = this.dropZones.get(dropTarget);
      if (dropZoneData) {
        if (isAltPressed && this.isChatTextareaDropTarget(dropTarget)) {
          this.animateNodeBackToOriginalPosition(currentState.dragData);
        }
        dropZoneData.callback(currentState.dragData);
      }
    } else if (isAltPressed && currentState.dragData) {
      this.animateNodeBackToOriginalPosition(currentState.dragData);
    }

    this.isDragging = false;
    this.dragState$.next({
      isDragging: false,
      dragData: null,
      mousePosition: { x: 0, y: 0 },
      altKeyPressed: false
    });

    this.removeDragElement();
    document.body.classList.remove('is-dragging');
  }

  registerDropZone(element: HTMLElement, callback: (data: DragDropData) => void): void {
    this.dropZones.set(element, { callback });
    element.classList.add('drop-zone');
    element.style.position = 'relative';
  }

  unregisterDropZone(element: HTMLElement): void {
    this.dropZones.delete(element);
    element.classList.remove('drop-zone', 'drop-zone-active');
  }

  private createDragElement(data: DragDropData, _sourceElement: HTMLElement): void {
    this.dragElement = document.createElement('div');
    this.dragElement.textContent = data.nodeName;
    this.dragElement.style.cssText = `
      position: fixed;
      top: ${data.position.y - 10}px;
      left: ${data.position.x - 10}px;
      pointer-events: none;
      z-index: 999999;
      background: #557daa;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      opacity: 0.9;
      transform: rotate(-2deg);
      white-space: nowrap;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
    this.dragElement.classList.add('dragging-element');

    document.body.appendChild(this.dragElement);
  }

  private updateDragElementPosition(x: number, y: number): void {
    if (this.dragElement) {
      this.dragElement.style.left = `${x + 10}px`;
      this.dragElement.style.top = `${y - 30}px`;
    }
  }

  private updateDropZonesFeedback(x: number, y: number): void {
    const dropTarget = this.getDropTargetAtPosition(x, y);

    this.dropZones.forEach((_, zone) => {
      if (zone === dropTarget) {
        zone.classList.add('drop-zone-active');
      } else {
        zone.classList.remove('drop-zone-active');
      }
    });
  }

  private getDropTargetAtPosition(x: number, y: number): HTMLElement | null {
    for (const [zone] of this.dropZones) {
      const rect = zone.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return zone;
      }
    }
    return null;
  }

  private removeDragElement(): void {
    if (this.dragElement) {
      document.body.removeChild(this.dragElement);
      this.dragElement = null;
    }

    this.dropZones.forEach((_, zone) => {
      zone.classList.remove('drop-zone-active');
    });
  }

  private isChatTextareaDropTarget(element: HTMLElement): boolean {
    return element.closest('selfai-platform-chat-message-input') !== null ||
           element.classList.contains('chat-input') ||
           element.tagName.toLowerCase() === 'textarea';
  }

  private animateNodeBackToOriginalPosition(dragData: DragDropData): void {
    if (!dragData.sourceElement || !dragData.originalPosition) {
      return;
    }

    const sourceElement = dragData.sourceElement;
    const originalRect = sourceElement.getBoundingClientRect();
    const currentPosition = {
      x: originalRect.left + originalRect.width / 2,
      y: originalRect.top + originalRect.height / 2
    };

    const tempElement = document.createElement('div');
    tempElement.textContent = dragData.nodeName;
    tempElement.style.cssText = `
      position: fixed;
      top: ${currentPosition.y - 10}px;
      left: ${currentPosition.x - 10}px;
      pointer-events: none;
      z-index: 999999;
      background: #557daa;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      opacity: 0.9;
      transform: rotate(-2deg);
      white-space: nowrap;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    `;

    document.body.appendChild(tempElement);

    requestAnimationFrame(() => {
      tempElement.style.transform = 'rotate(0deg) scale(0.8)';
      tempElement.style.opacity = '0.3';
    });

    setTimeout(() => {
      if (tempElement.parentNode) {
        document.body.removeChild(tempElement);
      }
      this.triggerCanvasRerender();
    }, 350);
  }

  private triggerCanvasRerender(): void {
    try {
      if (typeof (window as any).jsPlumb !== 'undefined') {
        (window as any).jsPlumb.repaintEverything();
      }

      const canvasService = (window as any).angularCanvasService;
      if (canvasService && canvasService.render) {
        canvasService.render();
      }
    } catch (error) {
      console.warn('Could not trigger canvas rerender:', error);
    }
  }
}
